import UnitPageShell from '../components/shared/UnitPageShell';
import React from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Paper, Typography, List, ListItem, ListItemText, Button, Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { slugToNavId } from '../utils/navIds';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getCardSx } from '../theme/tokens';
import { buildApiActionUrl } from '../utils/apiConfig';
// unlock-levels mapping (unitId -> required level). Edit src/data/unlock_levels.json to customise.
import unlockLevelsJson from '../data/unlock_levels.json';
// units_merged.json is not used here; authoritative navigation is used instead
import navMenu from '../data/navigation_menu_authoritative.json';

// Map of known id -> slug overrides (keeps parity with ModuleDropdown)
const slugOverrides: Record<string, string> = {
  PROGRAMMIERUNG_DATENTYPEN: 'm10_u1_x1_',
  PROGRAMMIERUNG_FUNKTIONEN: 'm10_u1_x2_funktionen',
  PROGRAMMIERUNG_AUFGABEN_FUP: 'm10_u2_x1_fup_uebung'
};

// build mapping of navId -> slug from actual unit files
const unitContext = (require as any).context('../pages/units', false, /\.tsx$/);
const SLUG_BY_ID: Record<string, string> = {};
unitContext.keys().forEach((key: string) => {
  const slug = key.replace('./', '').replace(/\.tsx$/, '');
  const navId = slugToNavId(slug);
  if (navId) {
    const existing = SLUG_BY_ID[navId];
    if (!existing || slug.length < existing.length) {
      SLUG_BY_ID[navId] = slug;
    }
  }
});

const ModulePage: React.FC = () => {
  const { modId } = useParams<{ modId: string }>();
  const { darkMode } = useTheme();
  // derive units/pages from authoritative navigation JSON (keeps ordering)
  // and enrich with available slug (if a real unit page exists)
  const units = React.useMemo<Array<any>>(() => {
    if (!modId) return [] as Array<any>;
    const menu: any[] = (navMenu as any) || [];
    const modEntry = menu.find(m => m.id === modId);
    if (!modEntry) return [] as Array<any>;

    // build list of units with their pages
    const result = (modEntry.children || []).map((unit: any) => {
      const pages = (unit.children || []).map((p: any) => {
        const pageId = p.id;
        const slug = slugOverrides[pageId] || SLUG_BY_ID[pageId] || null;
        return { id: pageId, title: p.title || pageId, slug };
      });
      return { id: unit.id, title: unit.title || unit.id, pages };
    });
    return result;
  }, [modId]);
  const [sets, setSets] = React.useState<string[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // load all quiz sets and show those that contain the modId prefix (convention)
    const load = async () => {
      setLoading(true); setError(null);
      try {
        const res = await fetch(buildApiActionUrl('getQuizList'));
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Fehler');
        const list: string[] = Array.isArray(data) ? data : data.sets || [];
        // simple heuristic: sets containing modId (e.g. "M3_U1_set")
        const filtered = list.filter(s => modId ? s.includes(modId) : true);
        setSets(filtered);
      } catch (e: any) { setError(e?.message || 'Fehler'); }
      finally { setLoading(false); }
    };
    load();
  }, [modId]);

  const auth = useAuth();
  const canBypassLevels = auth.canEditContent;
  const userLevel = auth.userLevel ?? 0;
  const totalScore = (auth.totalScore ?? 0);
  const canAccessM10 = canBypassLevels || totalScore >= 1000;
  const isM10Module = React.useMemo(() => {
    if (!modId) return false;
    const menu: any[] = (navMenu as any) || [];
    const modEntry = menu.find(m => m.id === modId);
    if (!modEntry) return false;
    const id = (modEntry.id || '').toString().toLowerCase();
    const title = (modEntry.title || '').toString().toLowerCase();
    if (id.includes('m10') || id.includes('programmierung') || title.includes('programmierung') || title.includes('codesys')) return true;
    // check children actionIds
    if (Array.isArray(modEntry.children)) {
      for (const c of modEntry.children) {
        if (c.actionId && (c.actionId.toString().includes('CODESYS') || c.actionId.toString().includes('PROGRAMMIERUNG'))) return true;
      }
    }
    return false;
  }, [modId]);
  const unlockLevels: Record<string, number> = (unlockLevelsJson as any) || {};

  if (!modId) return React.createElement(Typography, null, 'Kein Modul angegeben');

  // Enforce M10 access rule like ModuleDropdown: require >=1000 points or admin
  if (isM10Module && !canAccessM10) {
    return React.createElement(UnitPageShell, { title: `Modul ${modId}` },
      React.createElement('div', null,
        React.createElement('div', { style: { padding: 16 } },
          React.createElement('div', null,
            React.createElement('div', null, React.createElement('div'))
          )
        ),
        React.createElement('div', null,
          React.createElement('div', null,
            React.createElement(
              'div',
              null,
              React.createElement(
                'div',
                null,
                React.createElement(
                  'div',
                  null,
                  React.createElement(
                    require('@mui/material').Alert,
                    { severity: 'warning' },
                    `Für dieses Modul benötigst du mindestens 1000 Punkte. Deine aktuellen Punkte: ${totalScore}`
                  ),
                  React.createElement(Navigate, { to: '/', replace: true })
                )
              )
            )
          )
        )
      )
    );
  }

  return React.createElement(UnitPageShell, { title: `Modul ${modId}` },
    React.createElement(Paper, { sx: { ...getCardSx(darkMode), p: 3 } },
      React.createElement(Typography, { variant: 'h5', gutterBottom: true }, `Modul ${modId}`),
      React.createElement(Typography, { variant: 'body2', sx: { mb: 2 } }, 'Unterseiten / Quizze basierend auf der Android-Struktur:'),
      React.createElement(List, null,
        units.map(u => {
          const requiredLevel = unlockLevels[u.id] ?? 0;
          const allowed = canBypassLevels || (userLevel >= requiredLevel);

          return React.createElement(ListItem, { key: u.id },
            React.createElement(ListItemText, { primary: u.title, secondary: `${u.id} ${requiredLevel ? `(frei ab Level ${requiredLevel})` : ''}` }),
            React.createElement(Box, { sx: { ml: 2, display: 'flex', flexDirection: 'column', gap: 1 } },
              // render pages (sub-items) for this unit
              u.pages.map((p: any) => {
                const pageAllowed = allowed; // page-level unlocking not modelled separately here
                return React.createElement(ListItem, { key: p.id, sx: { pl: 0 } },
                  React.createElement(ListItemText, { primary: p.title, secondary: p.id }),
                  React.createElement(Box, { sx: { display: 'flex', gap: 1 } },
                    // Seite link if slug exists
                    p.slug ? React.createElement(RouterLink, { to: '/units/' + p.slug, style: { textDecoration: 'none' } }, React.createElement(Button, { variant: 'outlined', disabled: !pageAllowed }, 'Seite')) : React.createElement(Button, { variant: 'outlined', disabled: true }, 'Seite fehlt'),
                    // Quiz starten uses page id to resolve set
                    React.createElement(Button, { onClick: async (e: any) => {
                      e.preventDefault();
                      if (!pageAllowed) return;
                      const qs = await (await import('../services/quizService')).default.resolveQuizSetForUnit(p.id);
                      if (qs) window.location.href = '/quizzes/set/' + encodeURIComponent(qs) + '/play';
                      else window.location.href = '/quizzes/' + p.id + '/play';
                    }, variant: 'outlined', disabled: !pageAllowed }, 'Quiz starten')
                  )
                );
              })
            )
          );
        })
      ),

      React.createElement(Typography, { variant: 'h6', sx: { mt: 3 } }, 'Server-Quiz-Sets (passend zum Modul)'),
      loading ? React.createElement(Typography, null, 'Lade...') : (
        error ? React.createElement(Typography, { color: 'error' }, error) : (
          React.createElement(List, null,
            (sets || []).map(s => React.createElement(ListItem, { key: s, secondaryAction: React.createElement(RouterLink, { to: '/quizzes/set/' + encodeURIComponent(s) + '/play', style: { textDecoration: 'none' } }, React.createElement(Button, { variant: 'contained' }, 'Spiel starten')) },
                  React.createElement(ListItemText, { primary: s })
                ))
          )
        )
      ),

      React.createElement(Box, { sx: { mt: 2 } }, React.createElement(RouterLink, { to: '/', style: { textDecoration: 'none' } }, React.createElement(Button, { variant: 'text' }, 'Zurück zur Übersicht')))
    )
  );
};

export default ModulePage;
